import React, {FC} from "react";
import Divider from "../divider";
import Spacer from "../spacer";
import VidaSection from "../../../../components/@vida/vida-section";
import Note from "../note";

const SPAPlatformAndLaterVDSFaultTracing: FC = () => {
  return <div>
    <VidaSection title="" id="spa-platform-and-later-vds-fault-tracing">
      <Divider/>
      <h1>SPA Platform and Later (VDS) Fault Tracing</h1>

      {/* Fault tracing a Vehicle using wiring diagrams */}
      <Spacer/>
      <VidaSection title="Fault tracing a Vehicle using wiring diagrams"
                   id="fault-tracing-a-vehicle-using-wiring-diagrams-2">
        <p><strong>Fault tracing a vehicle using wiring diagrams</strong></p>
        <p>This procedure describes how to fault trace a VDS protocol-based vehicle, using the ranked list and the
          wiring diagram. The procedure covers how you can use the wiring diagram to locate a faulty wire.</p>
        <Note body={<p>This procedure is only applicable for VDS protocol-based vehicles.</p>}/>
        <p>This procedure is based on a case where a customer has a problem with his vehicle. A function in the vehicle
          does not work properly. The vehicle is already connected to the VIDA workstation and DTCs have been read out.
          No CSC exists from earlier preplanning.</p>
        <p><strong>Add CSC to work list</strong></p>
        <ol>
          <li>Expand the <strong>Work List</strong> menu item in the left-hand menu panel and select <strong>Customer
            Symptom Codes</strong>.<br/>{"->"} The Customer Symptom Codes page with a list of CSC is opened.
          </li>
          <li>Type a search string in the search field for the CSC you need to add and press [Enter] on your
            keyboard.<br/>{"->"} The CSC matching the search string you entered is shown in the right-hand panel.
          </li>
          <li>Select CSC by using the checkboxes and click the <strong>Add to List</strong> button.<br/>{"->"} A pop-up
            is shown where you can enter a name and create a new work list.
          </li>
          <li>Enter a list name and click the <strong>Create</strong> button.<br/>{"->"} The work list is created and
            the CSC is added to the new work list.
          </li>
        </ol>
        <p><strong>Use the wiring diagram</strong></p>
        <ol start={5}>
          <li>In this case, the fault cause information for the component at the top of the ranked list gives you the
            information that a circuit is open. The most probable reason for this is that there is something wrong along
            the signal. To fault trace the open circuit, select the component at the top of the list and open
            the <strong>Wiring Diagrams</strong> tab.<br/>{"->"} The wiring diagram for the selected function is shown.
            The component at the top of the ranked list is selected and therefore marked in the wiring diagram. Hover
            the cursor over the wire to see the signal ID.
          </li>
          <li>To view the signal information, right click the wire and select <strong>Signal
            information</strong>.<br/>{"->"} A pop-up is shown.
          </li>
          <li>Find out which components the signal goes between and how to measure the signal and then close the
            pop-up.
          </li>
          <li>Right click the wire again and select <strong>Cable harness image</strong>.<br/>{"->"} A pop-up is shown.
          </li>
          <li>The endpoints for the wire are marked. You can click them to see images of the endpoints. Close the
            pop-up.
          </li>
          <li>Inspect the wire and connector visually in the vehicle. Measure it according to measurement information in
            the signal information. Replace the wire or connector if needed. If no replacement is needed, continue by
            fault tracing the next component in the ranked list.
          </li>
        </ol>
      </VidaSection>

      {/* VIN Profiling */}
      <Spacer/>
      <VidaSection title="VIN Profiling"
                   id="vin-profiling-2">
        <p><strong>Selecting a vehicle in VIDA</strong></p>
        <p>This procedure describes how to select a vehicle.</p>
        <Note
          body={<><p>To perform fault tracing and software download, the vehicle needs to be connected. Select a vehicle
            if a vehicle is already connected and occupied by another user or if you only need to see information or
            work with pre- planning for the vehicle.</p><p>If a previous readout is available for the vehicle it is
            possible to access the <strong>Network, Fault Tracing and Vehicle Communication</strong> pages in an offline
            diagnostic mode and see the previously readout information.</p></>}/>
        <ol>
          <li>Open any sub tab under the <strong>Home</strong> tab.</li>
          <li>Click the <strong>Select</strong> button for the vehicle you want to work with.<br/>{"->"} The vehicle is
            selected and assigned its own tab.
          </li>
        </ol>
        <p>For further information and instructions on how to physically connect a vehicle to VIDA, see Vehicle
          communication how-to{"'"}s.</p>
      </VidaSection>

      {/* Troubleshooting a cable connection (VDS) */}
      <Spacer/>
      <VidaSection title="Troubleshooting a cable connection (VDS)"
                   id="troubleshooting-a-cable-connection-vds">
        <p><strong>Troubleshooting a cable connection (VDS)</strong></p>
        <Note body={<p>This procedure is only applicable for VDS protocol-based vehicles.</p>}/>
        <p>If a VDS protocol-based vehicle does not show up under the Connected Vehicles tab, there is built-in support
          inside the vehicle to troubleshoot this. In Sensus Connect there is a hidden menu that shows connection
          information. To access this menu:</p>
        <ol>
          <li>Open the <strong>Climate</strong> controls in full screen mode.</li>
          <li>Tap and hold the <strong>AC off</strong> and <strong>Auto climate</strong> buttons simultaneously for 10
            seconds.
          </li>
          <li>The menu appearing has advanced fault tracing information about:<br/>
            <ul>
              <li>Whether the vehicle is connected using Wi-Fi or the Ethernet cable.</li>
              <li>If connected using Ethernet it is shown whether it{"'"}s a networked or a peer-to-peer connection.
              </li>
              <li>If connected using Wi-Fi the signal strength perceived by the vehicle is shown.</li>
              <li>Whether the vehicle is locally or remotely connected.</li>
              <li>Whether the DoIP session is inactive, in announcement phase or in the session phase.</li>
            </ul>
          </li>
        </ol>
        <Note body={<p>As with any vehicle function, the diagnostic communication function may be disabled if the charge
          level of the vehicle is too low. Make sure that the vehicle is either fully charged or connected to a power
          unit before establishing the connection.</p>}/>
      </VidaSection>

      {/* SPA CMA VIDA CSC fault tracing descriptive */}
      <Spacer/>
      <VidaSection title="SPA CMA VIDA CSC fault tracing descriptive"
                   id="spa-cma-vida-csc-fault-tracing-descriptive">
        <p><strong>VIDA CSC based fault tracing SPA / CMA</strong></p>
        <p>It would not be uncommon to use the diagnostics from the left side of the VIDA screen especially if you are
          familiar with P3 and earlier models fault tracing. SPA and CMA platform cars departs from this method
          using <strong><em>Customer Symptom based fault tracing,</em></strong> which will require you to create a
          worklist. Attempting to use the older method would leave you with an incomplete screen, greyed out with any
          items selectable.</p>
        <p>To enter Fault tracing for a SPA or CMA platform car start by connecting the car.</p>
        <p>Then from the VIDA screen you <strong>must create a worklist</strong>.</p>
        <img
          src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20(VDS)%20Fault%20Tracing/SPA%20CMA%20VIDA%20CSC%20fault%20tracing%20descriptive_image1.jpg"
          alt="SPA CMA VIDA CSC fault tracing descriptive_image1"/>
        <p>The below example I typed in a keyword “ <strong>shift”–</strong> if for example a customer had a
          transmission
          shifting concern<strong>.</strong> Then select what represents what the customers concern is. In this example
          I <em>selected “shifts occur to early, too late, too often:” etc...</em></p>
        <img
          src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20(VDS)%20Fault%20Tracing/SPA%20CMA%20VIDA%20CSC%20fault%20tracing%20descriptive_image2.jpg"
          alt="SPA CMA VIDA CSC fault tracing descriptive_image2"/>
        <p>Clicking on the stethoscope <strong>“guidance”</strong> this enters you into guided fault tracing.</p>
        <p>In the example below you see there is comprehensive fault tracing available. Additionally, if there was a
          Tech
          Journal that would be indicated in the top bar – although in this case there is not. Tech Journals are found
          in
          the Bookstore (Volvo Tech Info) Service Information Library _</p>
        <img
          src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20(VDS)%20Fault%20Tracing/SPA%20CMA%20VIDA%20CSC%20fault%20tracing%20descriptive_image3.jpg"
          alt="SPA CMA VIDA CSC fault tracing descriptive_image3"/>
      </VidaSection>

      {/* Finding the location of a component */}
      <Spacer/>
      <VidaSection title="Finding the location of a component"
                   id="finding-the-location-of-a-component">
        <p><strong>Finding the location of a component</strong></p>
        <Note body={<p>This procedure is only applicable for VDS protocol-based vehicles.</p>}/>
        <ol>
          <li>Open the <strong>Planning &amp; Diagnostics</strong> tab and select <strong>Fault Tracing</strong> in the
            left-hand menu panel.
          </li>
          <li>Select a function in which the component is included from the drop-down menu. <br/>{"->"} The ranked list
            in the middle panel is populated.
          </li>
          <li>Select the <strong>Wiring Diagrams</strong> tab. In the ranked list, select the component that you are
            looking for.<br/>{"->"} A wiring diagram where the component is included is opened.
          </li>
          <li>Right click the component symbol in the wiring diagram and select <strong>Component
            images</strong>.<br/>{"->"} The Component images pop-up is shown.
          </li>
          <li>Select <strong>Location image</strong> from the drop-down menu.<br/>{"->"} The location of the component
            is shown in the location image.
          </li>
        </ol>
      </VidaSection>

      {/* Fault Tracing a Vehicles Network */}
      <Spacer/>
      <VidaSection title="Fault Tracing a Vehicles Network"
                   id="fault-tracing-a-vehicles-network-2">
        <p><strong>Fault tracing a vehicle{"'"}s network</strong></p>
        <p>This procedure describes how to fault trace a VDS protocol-based vehicle{"'"}s network. The description
          includes
          information about how the information for a network in the vehicle is presented in the ranked list and how to
          work with the ranked list to find a solution.</p>
        <Note body={<p>This procedure is only applicable for VDS protocol-based vehicles.</p>}/>
        <p>The procedure is based on a case where a customer has been complaining that her vehicle, an XC90 2016 is
          behaving strangely. Some features in the vehicle do not always work properly. You are about to start fault
          tracing the vehicle that is in the workshop and connected to the VIDA workstation.</p>
        <p><strong>Connect the vehicle</strong></p>
        <ol>
          <li>Start by connecting the vehicle via cable or
            wirelessly.<br/>{"->"} The <strong>Planning &amp; Diagnostics</strong> tab is opened by default as soon as
            the vehicle is connected and assigned its own tab. A network fault is indicated by the <img className="icon"
                                                                                                        src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20(VDS)%20Fault%20Tracing/Fault%20Tracing%20a%20Vehicles%20Network_image1.jpg"
                                                                                                        alt="Fault Tracing a Vehicles Network_image1"/> icon
            next to the Network tab in the left-hand menu panel. A network fault can generate a lot of unclear faults
            and should therefore always be prioritized and remedied first.
          </li>
        </ol>
        <p><strong>Check the ranked network list</strong></p>
        <ol start={2}>
          <li>Select the <strong>Network</strong> tab in the left-hand menu panel.<br/>{"->"} The Network page is
            opened. All networks in the vehicle are shown in the ranked list in the middle panel. The <img
              className="icon"
              src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20(VDS)%20Fault%20Tracing/Fault%20Tracing%20a%20Vehicles%20Network_image2.jpg"
              alt="Fault Tracing a Vehicles Network_image2"/> icon indicates a network that has components with set
            DTCs. Networks without this symbol does not have any DTCs. The list is ranked, meaning that the network that
            is most likely to have faults is presented at the top of the list.
          </li>
          <li>Select the network with a <img className="icon"
                                             src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20(VDS)%20Fault%20Tracing/Fault%20Tracing%20a%20Vehicles%20Network_image2.jpg"
                                             alt="Fault Tracing a Vehicles Network_image2"/> icon and click the <img
            className="icon"
            src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20(VDS)%20Fault%20Tracing/Fault%20Tracing%20a%20Vehicles%20Network_image3.jpg"
            alt="Fault Tracing a Vehicles Network_image3"/> to see the components it consists of.<br/>{"->"} The
            selected network in the list is expanded and you can see all components it consists of.
          </li>
          <li>In this case the first action in the list to check is a wire. Since the wire is presented first in the
            list it is the most probable cause of the problem. The best way to find out more about the wire is by using
            the wiring diagram. Select the wire in the selected network{"'"}s ranked list.
          </li>
        </ol>
        <p><strong>Start fault tracing</strong></p>
        <ol start={5}>
          <li>To find out where the wire is located in the wiring diagram, click the <strong>Wiring
            Diagrams</strong> tab.<br/>{"->"} The wiring diagram is shown with the signal marked in bold. A dashed line
            in the wiring diagram indicates that the wire is most likely the cause of the problem.
          </li>
          <li>Right click the dashed line and select <strong>Signal information</strong>.<br/>{"->"} A pop-up is opened.
          </li>
          <li>Identify what the wire is connected to and how to measure the signal and then close the pop-up.</li>
          <li>Right click the wire (the dashed line) once again and select <strong>Cable harness
            image</strong>.<br/>{"->"} A pop-up is opened.
          </li>
          <li>Identify where in the vehicle the wire is located. The connector is marked in green. Measure the signal
            and do a visual inspection of the pin and the wire in the vehicle to identify the faults. Remedy if needed.
          </li>
          <li>If no fault was found, continue with the next component in the ranked list.</li>
        </ol>
      </VidaSection>
    </VidaSection>
    <style jsx>{`
      img {
        width: 100%;
      }

      .icon {
        width: 25px;
      }
    `}</style>
  </div>
}

export default SPAPlatformAndLaterVDSFaultTracing;
